import { createRef, CSSProperties, Fragment, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { v4 as uuidv4 } from "uuid";

import { classNames } from "primereact/utils";

import {
  faFile,
  faFileAudio,
  faFileCsv,
  faFileExcel,
  faFileGif,
  faFileJpg,
  faFileMov,
  faFileMp3,
  faFileMp4,
  faFilePdf,
  faFilePng,
  faFileText,
  faFileVideo,
  faFileWaveform,
  faFileWord,
  IconDefinition,
} from "@fortawesome/pro-light-svg-icons";
import { faCloudArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translationKeys } from "../../../../models/app-translation-keys";
import { UploadFile } from "../../../../models/classes/upload-file.class";
import { FileUploadError } from "../../../../models/enums/file-upload-error.enum";

import "./FileUploadArea.scss";

export const possibleFileTypes: IFileType[] = [
  { labelKey: "video", value: "video/*", icon: faFileVideo },
  { labelKey: "audio", value: "audio/*", icon: faFileAudio },
  { labelKey: "jpeg", value: "image/jpeg", icon: faFileJpg },
  { labelKey: "jpg", value: "image/jpeg", icon: faFileJpg },
  { labelKey: "png", value: "image/png", icon: faFilePng },
  { labelKey: "gif", value: "image/gif", icon: faFileGif },
  { labelKey: "pdf", value: "application/pdf", icon: faFilePdf },
  { labelKey: "doc", value: "application/msword", icon: faFileWord },
  {
    labelKey: "docx",
    value: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    icon: faFileWord,
  },
  { labelKey: "xls", value: "application/vnd.ms-excel", icon: faFileExcel },
  {
    labelKey: "xlsx",
    value: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    icon: faFileExcel,
  },
  { labelKey: "txt", value: "text/plain", icon: faFileText },
  { labelKey: "csv", value: "text/csv", icon: faFileCsv },
  { labelKey: "mp3", value: "audio/mpeg", icon: faFileMp3 },
  { labelKey: "wav", value: "audio/wav", icon: faFileWaveform },
  { labelKey: "mp4", value: "video/mp4", icon: faFileMp4 },
  { labelKey: "avi", value: "video/x-msvideo", icon: faFileVideo },
  { labelKey: "mov", value: "video/quicktime", icon: faFileMov },
];

interface IFileUploadAreaProps {
  disabled?: boolean;
  uploadFiles: UploadFile[];
  showAllowedFileTypes?: boolean;
  existingFilesNames?: string[];
  allowedFileTypes?: IFileType[];
  fileSizeLimitMB: number;
  isSingleFileUpload?: boolean;
  maxConcurrentProcessedFiles?: number;
  smallUploadArea?: boolean;
  styles?: CSSProperties;
  onFilesSelected: (files: UploadFile[]) => void;
}

export interface IFileType {
  labelKey: string;
  value: string;
  icon: IconDefinition;
}

const RawFileUploadArea: FunctionComponent<IFileUploadAreaProps> = (props: IFileUploadAreaProps) => {
  const { t } = useTranslation();
  const inputRef = createRef<HTMLInputElement>();
  const [isUploadAreaDisabled, setIsUploadAreaDisabled] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  const allowedFileTypes = props.allowedFileTypes ?? possibleFileTypes;

  const handleFileChange = (event: any) => {
    if (props.disabled) {
      return;
    }

    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const newFiles = Array.from(selectedFiles) as File[];
      const newUploadFiles = paresFiles(newFiles);
      const validatedFiles = validateFiles(newUploadFiles, props.existingFilesNames);
      props.onFilesSelected(validatedFiles);
    }
    resetSelectedFileInput(event);
  };

  const handleDragEnter = (event: any) => {
    event.preventDefault();
    if (!isUploadAreaDisabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: any) => {
    // only clear the state when the cursor actually exits the drop zone entirely
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      inputRef.current?.click();
    }
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    setIsDragOver(false);

    if (props.disabled) {
      return;
    }

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const newFiles = Array.from(droppedFiles) as File[];
      const newUploadFiles = paresFiles(newFiles);
      const validatedFiles = validateFiles(newUploadFiles, props.existingFilesNames);
      props.onFilesSelected(validatedFiles);
    }
    resetSelectedFileInput(event);
  };

  const resetSelectedFileInput = (event: any) => {
    event.target.value = "";
  };

  const validateFiles = (uploadFiles: UploadFile[], existingFilesNames?: string[]) => {
    return uploadFiles.map(uploadFile => {
      uploadFile.errors = uploadFile.errors ?? [];

      const uploadFileType = uploadFile.file.type?.length
        ? uploadFile.file.type
        : "." + uploadFile.file.name.split(".").pop() || ".*";

      if (uploadFile.file.size > props.fileSizeLimitMB * 1024 * 1024) {
        uploadFile.errors = [...uploadFile.errors, FileUploadError.MAX_FILE_SIZE_EXCEEDED];
      }

      if (
        !allowedFileTypes.some(fileType => fileType.value === uploadFileType) &&
        !allowedFileTypes.some(fileType => fileType.value === "video/*" && uploadFile.file.type.startsWith("video/")) &&
        !allowedFileTypes.some(fileType => fileType.value === "audio/*" && uploadFile.file.type.startsWith("audio/"))
      ) {
        uploadFile.errors = [...uploadFile.errors, FileUploadError.INVALID_FILE_TYPE];
      }
      if (existingFilesNames?.some(fileName => fileName === uploadFile.file.name)) {
        uploadFile.errors = [...uploadFile.errors, FileUploadError.FILE_ALREADY_EXISTS];
      }

      return uploadFile;
    });
  };

  const paresFiles = (files: File[]): UploadFile[] => {
    return files.map(
      file =>
        new UploadFile({
          id: uuidv4(),
          file,
          icon: getFileIcon(file),
          fileSizeInByte: file.size,
        }),
    );
  };

  const getFileIcon = (file: File): IconDefinition => {
    const foundFileType = possibleFileTypes.find((fileType: IFileType) => file.type === fileType.value);
    return foundFileType ? foundFileType.icon : faFile;
  };

  useEffect(() => {
    setIsUploadAreaDisabled(
      props.disabled ||
        (!!props.isSingleFileUpload && !!props.uploadFiles.length) ||
        (!!props.maxConcurrentProcessedFiles && props.uploadFiles?.length >= props.maxConcurrentProcessedFiles),
    );
  }, [props.disabled, props.isSingleFileUpload, props.uploadFiles]);

  return (
    <div
      className={classNames("file-drop-area", {
        disabled: isUploadAreaDisabled,
        "drag-over": isDragOver,
      })}
      style={props.styles}
      onDrop={handleDrop}
      onDragOver={event => event.preventDefault()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onKeyDown={handleKeyDown}
      onClick={() => inputRef.current?.click()}
      tabIndex={0}
      role="button"
    >
      <div
        className={classNames("upload-info", {
          "small-area": props.smallUploadArea,
        })}
      >
        <label className="upload-icon-container" data-testid="file-upload-icon-button">
          <div className="upload-icon">
            <FontAwesomeIcon icon={faCloudArrowUp} />
          </div>
        </label>

        <div className="upload-text-container">
          <div className="upload-browse">
            <label className="upload-button-text" data-testid="file-upload-text-button">
              {t(translationKeys.upload.clickForUpload)}
            </label>
            &nbsp;
            <span>{t(translationKeys.upload.orDropFileIntoArea)}</span>
          </div>

          <div>
            {t(translationKeys.upload.allowedFileSize)}: {props.fileSizeLimitMB} MB
          </div>

          {props.showAllowedFileTypes && (
            <div>
              {t(translationKeys.upload.allowedFileTypesInformation)}
              {": "}
              {allowedFileTypes.map((fileType: IFileType, index: number) => (
                <Fragment key={index}>
                  {t(fileType.labelKey)}
                  {index !== allowedFileTypes.length - 1 && ", "}
                </Fragment>
              ))}
            </div>
          )}

          {!!props.maxConcurrentProcessedFiles && props.maxConcurrentProcessedFiles > 1 && (
            <div>
              {t(translationKeys.upload.allowedMaxFiles)}: {props.maxConcurrentProcessedFiles}
            </div>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        hidden
        id="browse"
        onChange={handleFileChange}
        accept={allowedFileTypes.map(fileType => fileType.value).join(",")}
        multiple={!props.isSingleFileUpload}
        disabled={isUploadAreaDisabled}
      />
    </div>
  );
};

export const FileUploadArea = observer(RawFileUploadArea);
